import {
  DependencyList,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { showException } from 'utils/exception-helper';

/**
 * Imperative API-call helpers backed by Orval-generated low-level hooks.
 *
 * These replace the legacy NSwag `useApiCall` / `useResponse` (from
 * `swaggerhooks`) while preserving the exact consumer-facing contract
 * (`run`/`advancedRun`/`response`/`error`/`status` and `useResponseFn().update`).
 * Instead of constructing an NSwag client, the caller passes an async function
 * — typically the result of an Orval `useXxxHook()` — so every request still
 * goes through the Orval axios mutator (auth header, token refresh, date
 * handling). Errors surface as toasts via `showException`, matching the rest of
 * the Orval/React-Query code paths.
 */

export enum RequestStatus {
  Idle,
  Fetching,
  Fetched,
  Error,
}

export interface UseApiCallOptions {
  hideError?: boolean;
  initialRequestStatus?: RequestStatus;
  clearResponseOnError?: boolean;
  /** Accepted for source compatibility with the legacy options shape. */
  hideConnectionError?: boolean;
  errorModalId?: string;
}

export interface ApiCall<Response, ExtraParams extends any[]> {
  run(...extra: ExtraParams): Promise<[Response, null] | [null, unknown]>;
  advancedRun(
    extra: ExtraParams,
    options?: UseApiCallOptions
  ): Promise<[Response, null] | [null, unknown]>;
  response?: Response;
  error?: unknown;
  status: RequestStatus;
  setResponse: React.Dispatch<React.SetStateAction<Response | undefined>>;
  setStatus: React.Dispatch<React.SetStateAction<RequestStatus>>;
}

interface State<Response> {
  response?: Response;
  error?: unknown;
  status: RequestStatus;
}

/**
 * Wrap an async function (e.g. an Orval `useXxxHook()` result) in the imperative
 * `ApiCall` interface previously provided by `useApiCall`.
 *
 * @deprecated Prefer the generated Orval React Query hooks (e.g. `useXxxMutation`
 * / `useXxx`) directly instead of wrapping the raw `useXxxHook()` in this shim.
 * This exists only as a migration bridge off the legacy `swaggerhooks` `useApiCall`.
 */
export const useApiCallFn = <Response, ExtraParams extends any[]>(
  methodCaller: (...extra: ExtraParams) => Promise<Response>,
  methodCallerDeps?: DependencyList,
  options?: UseApiCallOptions
): ApiCall<Response, ExtraParams> => {
  const [state, setState] = useState<State<Response>>({
    response: undefined,
    error: undefined,
    status: options?.initialRequestStatus ?? RequestStatus.Idle,
  });
  const requestCalls = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const capturedMethodCaller = useCallback(methodCaller, methodCallerDeps ?? []);

  const advancedRun = useCallback(
    async (
      extra: ExtraParams,
      runOptions?: UseApiCallOptions
    ): Promise<[Response, null] | [null, unknown]> => {
      setState((s) => ({ ...s, status: RequestStatus.Fetching }));
      requestCalls.current += 1;
      const myRequestNumber = requestCalls.current;

      try {
        const response = await capturedMethodCaller(...extra);

        // Ignore stale responses if a newer call has since started.
        if (myRequestNumber === requestCalls.current) {
          setState((s) => ({
            ...s,
            status: RequestStatus.Fetched,
            response,
            error: undefined,
          }));
        }
        return [response, null];
      } catch (err) {
        setState((s) => ({
          ...s,
          status: RequestStatus.Error,
          error: err,
          response: runOptions?.clearResponseOnError ? undefined : s.response,
        }));

        if (!runOptions?.hideError) {
          showException(err);
        }

        return [null, err];
      }
    },
    [capturedMethodCaller]
  );

  const run = useCallback(
    (...extra: ExtraParams): Promise<[Response, null] | [null, unknown]> =>
      advancedRun(extra, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advancedRun, options]
  );

  const setResponse: React.Dispatch<
    React.SetStateAction<Response | undefined>
  > = useCallback((setAction) => {
    requestCalls.current += 1;
    setState((s) => ({
      ...s,
      response:
        setAction instanceof Function ? setAction(s.response) : setAction,
      status: RequestStatus.Fetched,
    }));
  }, []);

  const setStatus: React.Dispatch<React.SetStateAction<RequestStatus>> =
    useCallback((setAction) => {
      setState((s) => ({
        ...s,
        status: setAction instanceof Function ? setAction(s.status) : setAction,
      }));
    }, []);

  return {
    run,
    advancedRun,
    response: state.response,
    error: state.error,
    status: state.status,
    setResponse,
    setStatus,
  };
};

export interface ApiResponse<Response> {
  response: ApiCall<Response, []>['response'];
  error: ApiCall<Response, []>['error'];
  status: ApiCall<Response, []>['status'];
  setStatus: ApiCall<Response, []>['setStatus'];
  update(
    response?: React.SetStateAction<Response | undefined>,
    options?: UseApiCallOptions
  ): Promise<[Response, null] | [null, unknown] | undefined>;
}

/**
 * Auto-running query variant: calls `methodCaller` on mount and whenever `deps`
 * change (mirrors the legacy `useResponse`). Returns `response`/`status`/`error`
 * plus an `update()` that either re-runs the call or sets the cached response.
 *
 * @deprecated Prefer the generated Orval React Query hooks (e.g. `useXxx` with
 * `refetch`) directly instead of this shim. This exists only as a migration
 * bridge off the legacy `swaggerhooks` `useResponse`.
 */
export const useResponseFn = <Response>(
  methodCaller: () => Promise<Response>,
  deps?: DependencyList,
  options?: UseApiCallOptions
): ApiResponse<Response> => {
  const apiCall = useApiCallFn(methodCaller, deps, {
    ...options,
    initialRequestStatus:
      options?.initialRequestStatus ?? RequestStatus.Fetching,
  });

  useEffect(() => {
    apiCall.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? []);

  const update = useCallback(
    async (
      response?: React.SetStateAction<Response | undefined>,
      updateOptions?: UseApiCallOptions
    ) => {
      if (response !== undefined) {
        apiCall.setResponse(response);
        return undefined;
      }
      return apiCall.advancedRun([], updateOptions ?? options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiCall.setResponse, apiCall.advancedRun]
  );

  return {
    response: apiCall.response,
    status: apiCall.status,
    error: apiCall.error,
    setStatus: apiCall.setStatus,
    update,
  };
};
